// Localização: frontend/src/components/LandingPage/FeaturesSection.jsx
// Seção: Recursos Poderosos do Higeia.tech (Versão 1.0.0 - Com Animações)
import React from 'react';
import { motion } from 'framer-motion';
import { 
    Users, 
    Calendar, 
    Pill, 
    Monitor, 
    FileText, 
    BarChart3
} from 'lucide-react';

// Dados dos recursos
const features = [
    {
        title: 'Gestão Completa de Pacientes',
        description: 'Gerencie prontuários eletrônicos, planos de saúde e histórico de pacientes de forma centralizada e segura.',
        icon: Users,
        color: 'text-blue-600',
        bgColor: 'bg-blue-50'
    },
    {
        title: 'Agendamento e Tarefas Otimizadas',
        description: 'Crie, acompanhe e organize todas as tarefas e agendamentos com facilidade, garantindo que nada seja esquecido.',
        icon: Calendar,
        color: 'text-green-600',
        bgColor: 'bg-green-50'
    },
    {
        title: 'Controle Inteligente de Medicamentos',
        description: 'Mantenha um controle preciso do estoque de medicamentos, alertando sobre validades e reposições necessárias.',
        icon: Pill,
        color: 'text-purple-600',
        bgColor: 'bg-purple-50'
    },
    {
        title: 'Monitoramento de Equipamentos',
        description: 'Monitore o status e a localização de equipamentos, garantindo a disponibilidade e manutenção adequadas.',
        icon: Monitor,
        color: 'text-orange-600',
        bgColor: 'bg-orange-50'
    },
    {
        title: 'Diário de Cuidados Integrado',
        description: 'Registre e acompanhe diariamente os cuidados prestados, observações e evolução de cada paciente.',
        icon: FileText,
        color: 'text-indigo-600',
        bgColor: 'bg-indigo-50'
    },
    {
        title: 'Dashboards e Análises de Dados',
        description: 'Acesse painéis intuitivos com métricas e gráficos para tomadas de decisão baseadas em dados.',
        icon: BarChart3,
        color: 'text-teal-600',
        bgColor: 'bg-teal-50'
    },
];

const FeaturesSection = () => {
    return (
        <section id="recursos" className="py-20 sm:py-24 bg-white mt-16">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                
                {/* Cabeçalho da Seção */}
                <div className="text-center mb-16">
                    <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-gray-900">
                        Recursos Poderosos do Higeia.tech
                    </h2>
                    <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
                        Descubra o que torna o Higeia.tech a solução ideal para a gestão de home care e lares de idosos, 
                        otimizando seu tempo e elevando a qualidade do cuidado.
                    </p>
                </div>

                {/* Grid de Recursos com animações */}
                <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
                    {features.map((feature, index) => {
                        const Icon = feature.icon;
                        return (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{ delay: index * 0.1, duration: 0.5 }}
                                whileHover={{ y: -8, transition: { duration: 0.2 } }}
                                className="group relative bg-white rounded-xl md:rounded-2xl p-4 sm:p-6 md:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
                            >
                                {/* Ícone com fundo colorido e animação */}
                                <motion.div
                                    whileHover={{ scale: 1.1, rotate: 5 }}
                                    className={`inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 rounded-lg md:rounded-xl ${feature.bgColor} mb-3 sm:mb-4 md:mb-6 transition-transform duration-300`}
                                >
                                    <Icon className={`h-5 w-5 sm:h-6 sm:w-6 md:h-8 md:w-8 ${feature.color}`} />
                                </motion.div>

                                {/* Título */}
                                <h3 className="text-sm sm:text-base md:text-xl font-bold text-gray-900 mb-2 sm:mb-3">
                                    {feature.title}
                                </h3>

                                {/* Descrição */}
                                <p className="text-xs sm:text-sm md:text-base text-gray-600 leading-relaxed">
                                    {feature.description}
                                </p>

                                {/* Efeito de brilho sutil no hover */}
                                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-higeia-light/0 to-higeia-light/0 group-hover:from-higeia-light/5 group-hover:to-transparent transition-all duration-300 pointer-events-none" />
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default FeaturesSection;

